import { useEffect, useState } from "react";
import { useTranslation } from "../../context/TranslationContext";
import { AppScope } from "../ds/AppCover";
import AppIcon, { APP_COLORS, appKey } from "../ds/AppIcon";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import Modal from "../ds/Modal";
import PopUpButton from "../ds/PopUpButton";

const PANEL = "items-center !rounded-t-[32px] !pt-space-24 min-[600px]:!rounded-[32px] min-[600px]:!p-space-32 [&>div:first-child]:hidden";
const WHAT_HAPPENS = [
  ["apps", "happens1"],
  ["users", "happens2"],
  ["wallet", "happens3"],
];

/**
 * Figma "Add app · Confirm" and "Add app · Success": what adding does and who can open it,
 * then where to find it. Figma draws Mintly Cloud; every addable app follows it. `added` is
 * the copy just installed, which moves the popup to its second step.
 */
const AddAppModal = ({ app, spaceName, added, loading, isDark, onConfirm, onOpenAdded, onClose }) => {
  const { t } = useTranslation();
  const [visibility, setVisibility] = useState("public");

  useEffect(() => {
    if (app) setVisibility("public");
  }, [app]);

  if (!app) return null;
  const text = (name) => t(`appStorePage.apps.${app.id}.${name}`);
  const colors = APP_COLORS[appKey(app.id)];
  const whoLabel = (key) => t("appStorePage.whoCanOpen", { who: t(`appStorePage.${key}`) });

  return (
    <AppScope app={app.id}>
      <Modal
        open
        bare
        title={added ? t("appStorePage.successTitle", { app: app.storeName }) : t("appStorePage.confirmTitle", { app: app.storeName, space: spaceName })}
        onClose={() => !loading && onClose()}
        isDark={isDark}
        width="min-[600px]:w-[460px]"
        panelClass={PANEL}
      >
        {added ? (
          <>
            <span className="relative block size-16 shrink-0">
              <AppIcon app={app.id} size={64} glyph={32} />
              <span className="absolute -bottom-2 -right-2 flex size-[26px] items-center justify-center rounded-full bg-accent-default text-accent-on shadow-[0_0_0_3px_var(--color-bg-raised)]">
                <Icon name="check" size={14} />
              </span>
            </span>
            <div className="flex w-full flex-col items-center gap-space-4 text-center">
              <h2 className="text-title-2 text-label-primary">{t("appStorePage.successTitle", { app: app.storeName })}</h2>
              <p className="text-subheadline text-label-secondary">{text("successBody")}</p>
            </div>
            <div className="flex w-full flex-col gap-space-8">
              <Button fullWidth onClick={onOpenAdded}>
                {text("successAction")}
              </Button>
              <Button variant="plain" fullWidth onClick={onClose}>
                {t("appStorePage.backToStore")}
              </Button>
            </div>
          </>
        ) : (
          <>
            <AppIcon app={app.id} size={64} glyph={32} />
            <div className="flex w-full flex-col items-center gap-space-4 text-center">
              <h2 className="text-title-2 text-label-primary">
                {t("appStorePage.confirmTitle", { app: app.storeName, space: spaceName })}
              </h2>
              <p className="text-subheadline text-label-secondary">{t("appStorePage.confirmSubtitle")}</p>
            </div>
            <ul className="flex w-full flex-col gap-space-12 rounded-[22px] bg-bg-inset p-space-16">
              {WHAT_HAPPENS.map(([icon, key]) => (
                <li key={key} className="flex items-center gap-space-12">
                  <Icon name={icon} size={20} className={colors.label} />
                  <span className="min-w-0 flex-1 text-subheadline text-label-primary">{text(key)}</span>
                </li>
              ))}
            </ul>
            <PopUpButton
              label={t("appStorePage.whoCanOpen", { who: "" })}
              value={visibility}
              onChange={setVisibility}
              options={[
                { value: "public", label: whoLabel("whoMembers") },
                { value: "private", label: whoLabel("whoBuyers") },
              ]}
              className="w-full"
            />
            <div className="flex w-full flex-wrap-reverse gap-space-8 max-[599px]:flex-col-reverse max-[599px]:flex-nowrap">
              <Button variant="gray" disabled={loading} className="grow" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button loading={loading} className="grow" onClick={() => onConfirm(visibility)}>
                {text("addNamed")}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </AppScope>
  );
};

export default AddAppModal;
